import Constants from "expo-constants";
import React, { useCallback, useEffect, useMemo } from "react";
import { Alert } from "../../components/Alert";
import auth from "../../contexts/FirebaseAuth";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { PickerOptionsMode, useDocumentUpload } from "../../hooks/documents";
import { StackScreenProps } from "@react-navigation/stack";
import VStack from "../../ui_atoms/VStack";
import Gap from "../../ui_atoms/Gap";
import { useI18n, useNationalityFormatter } from "../../hooks/i18n-hooks";
import { ClientStatus, Document, DocumentType, ProfileType } from "../../__generated__/graphql";
import { useIntercom } from "../../hooks/intercom";
import { useMutation, useQuery } from "@apollo/client";
import { UPDATE_USER } from "../../graphql/identity";
import ListItem, { buildDescription } from "../../ui_atoms/ListItem";
import SimplePageContent from "../../components/SimplePageContent";
import ListTitle from "../../ui_atoms/ListTitle";
import Loading from "../../components/Loading";
import { UPDATE_CLIENT_PROFILE, UPDATE_PRIVATE_CLIENT_PROFILE } from "../../graphql/core";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { useTheme } from "../../hooks/use-theme";
import DocumentButton from "../../components/document/DocumentButton";
import { useWorkerDocumentManager } from "../../hooks/user-worker-document";
import DocumentEditor from "../../components/document/DocumentEditor";
import { HeaderButton } from "./ScreenHeader";
import { useDeleteAccount } from "../../hooks/use-delete-account";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { useGetIsWorkerProfileComplete } from "../../hooks/use-get-is-worker-profile-complete";
import { useDeleteCurrentProfile } from "../../hooks/use-delete-current-profile";
import AppVersion from "../../components/AppVersion";
import { Platform } from "react-native";
import { ProfileStackParamList } from "../../navigations/ProfileStack";
import { useDateFormatter } from "../../hooks/date";
import { useBuildIjmDescription } from "../../hooks/ijm";
import { GET_CLIENT_USERS } from "../../graphql/client";
import { useProfileIjmEditor } from "../../hooks/use-ijm-editor";

type ProfileToUse = "Worker" | "Client" | "PrivateClient" | "Unknown";

type ProfileScreenProps = StackScreenProps<RootStackParamList & ProfileStackParamList>;

const ProfileScreen = ({ navigation }: ProfileScreenProps) => {
    const i18n = useI18n();
    const { colors, gridSize } = useTheme();
    const [devClickCount, setDevClickCount] = React.useState(0);
    const [updatingAvatar, setUpdatingAvatar] = React.useState(false);
    const [updatingLogo, setUpdatingLogo] = React.useState(false);
    const [removingDocument, setRemovingDocument] = React.useState(false);
    const { user, refreshUser } = React.useContext(AuthManagerContext);
    const { profileType, openProfileSwitch } = React.useContext(ProfileSwitcherManagerContext);
    const currentProfile = useCurrentProfile();
    const buildIjmDescription = useBuildIjmDescription();
    const [updateUser, { error: saveAvatarError, reset: saveAvatarReset }] =
        useMutation(UPDATE_USER);
    const [updateClientProfile, { error: saveLogoError, reset: saveLogoReset }] =
        useMutation(UPDATE_CLIENT_PROFILE);
    const [
        updatePrivateClientProfile,
        { error: savePrivateLogoError, reset: savePrivateLogoReset },
    ] = useMutation(UPDATE_PRIVATE_CLIENT_PROFILE);

    const { data: { getClientUsers = [] } = {} } = useQuery(GET_CLIENT_USERS, {
        variables: {
            clientId: currentProfile?.client?.id || "",
        },
        skip: !currentProfile?.client?.id,
    });

    const onDeleteAccount = useDeleteAccount();
    const onDeleteWorkerProfile = useDeleteCurrentProfile({ profileType: ProfileType.Worker });
    const onDeleteClientProfile = useDeleteCurrentProfile({ profileType: ProfileType.Client });
    const onDeletePrivateClientProfile = useDeleteCurrentProfile({
        profileType: ProfileType.Client,
        isPrivateClient: true,
    });
    const getIsWorkerProfileComplete = useGetIsWorkerProfileComplete();
    const { isValid: isWorkerProfileComplete } = getIsWorkerProfileComplete(user);

    const isPrivateClient = currentProfile?.isPrivateClient || false;

    const { addWorkerDocument, removeWorkerDocument } = useWorkerDocumentManager();

    const [displayedDocument, setDisplayedDocument] = React.useState<Document | undefined>(
        undefined,
    );

    const uploadNewProfile = React.useCallback<Parameters<typeof useDocumentUpload>[0]["onSelect"]>(
        async ({ documentUrl }) => {
            try {
                setUpdatingAvatar(true);
                if (isPrivateClient) {
                    await updatePrivateClientProfile({
                        variables: { changes: { user: { avatarUrl: documentUrl } } },
                    });
                } else {
                    await updateUser({
                        variables: { userData: { avatarUrl: documentUrl } },
                    });
                }
                // this will force to update user and so refresh avatar
                await refreshUser?.();
            } finally {
                /* empty */
            }
        },
        [isPrivateClient, refreshUser, updatePrivateClientProfile, updateUser],
    );

    const uploadNewLogo = React.useCallback<Parameters<typeof useDocumentUpload>[0]["onSelect"]>(
        async ({ documentUrl }) => {
            try {
                await updateClientProfile({
                    variables: { changes: { client: { logoUrl: documentUrl } } },
                });
                // this will force to update user and so refresh avatar
                await refreshUser?.();
            } finally {
                /* empty */
            }
        },
        [updateClientProfile, refreshUser],
    );

    const { documentDropdownProps: profilePicturePickerProps } = useDocumentUpload({
        documentType: DocumentType.ProfilePicture,
        isPublic: true,
        modes: [PickerOptionsMode.Library, PickerOptionsMode.Camera, PickerOptionsMode.Web],
        setIsLoading: setUpdatingAvatar,
        onSelect: uploadNewProfile,
        webUploadFn: "profilePicture",
        enableCropZoom: true,
    });

    const { documentDropdownProps: companyPicturePickerProps } = useDocumentUpload({
        documentType: DocumentType.CompanyPicture,
        generateUniqueId: true,
        isPublic: true,
        modes: [PickerOptionsMode.Library, PickerOptionsMode.Camera, PickerOptionsMode.Web],
        setIsLoading: setUpdatingLogo,
        onSelect: uploadNewLogo,
        webUploadFn: "companyLogo",
        enableCropZoom: true,
    });

    const onAddWorkerDocument = React.useCallback(
        async (documentType: DocumentType, document: Document) => {
            setRemovingDocument(true);
            try {
                await addWorkerDocument(documentType, document);
                await refreshUser?.();
            } finally {
                setRemovingDocument(false);
            }
        },
        [addWorkerDocument, refreshUser],
    );

    const editWorkerProfile = useCallback(() => {
        navigation.navigate("WorkerProfileEdit");
    }, [navigation]);

    const editClientProfile = useCallback(() => {
        navigation.navigate("ClientProfileEdit");
    }, [navigation]);

    const editAccount = useCallback(() => {
        navigation.navigate("AccountEdit");
    }, [navigation]);

    const editPrivateClient = useCallback(() => {
        navigation.navigate("PrivateClientEdit");
    }, [navigation]);

    const editPhoneNumber = useCallback(() => {
        navigation.navigate("PhoneNumberEdit");
    }, [navigation]);

    const editClient = useCallback(() => {
        navigation.navigate("ClientEdit");
    }, [navigation]);

    const editBankAccount = useCallback(() => {
        navigation.navigate("BankAccountEdit");
    }, [navigation]);

    const editDiplomaAndTraining = useCallback(() => {
        navigation.navigate("DiplomaAndTrainingEdit");
    }, [navigation]);

    const toActivityLog = useCallback(() => {
        navigation.navigate("UserLogs");
    }, [navigation]);

    const toColleagues = useCallback(() => {
        navigation.navigate("Colleagues");
    }, [navigation]);

    const onLogout = useCallback(() => {
        Alert.alert(
            i18n.t("profile.logout_title"),
            i18n.t("profile.logout_description"),
            [
                {
                    text: i18n.t("profile.logout_confirm"),
                    onPress: () => {
                        void auth()
                            .signOut()
                            .then(() => {
                                if (Platform.OS === "web") {
                                    setTimeout(() => {
                                        // Reload the webapp page to prevent reCAPTCHA errors
                                        window?.location?.reload?.();
                                    }, 10);
                                }
                            });
                    },
                    style: "destructive",
                },
                {
                    text: i18n.t("profile.logout_cancel"),
                    style: "cancel",
                },
            ],
            { cancelable: true },
        );
    }, [i18n]);

    const isWorkerProfile = profileType === ProfileType.Worker;
    const isClientProfile = profileType === ProfileType.Client;
    const { presentIntercom } = useIntercom();
    const intlFormat = useDateFormatter();
    const civilState = useMemo(() => {
        if (!user || !isWorkerProfile) {
            return undefined;
        }
        return user.workerProfile?.maritalStatusFrom
            ? i18n.t("profile.civil_state", {
                  state: i18n.t(`user.${user.workerProfile?.maritalStatus || "single"}`),
                  date: intlFormat(new Date(user.workerProfile.maritalStatusFrom || "")),
              })
            : i18n.t("profile.civil_state_simple", {
                  state: i18n.t(`user.${user.workerProfile?.maritalStatus || "single"}`),
              });
    }, [user, isWorkerProfile, i18n, intlFormat]);

    const formatNationality = useNationalityFormatter();
    const { presentArticle } = useIntercom();

    useApolloErrorSnackbar(saveAvatarError, saveAvatarReset, i18n.t("profile.save_avatar_error"));
    useApolloErrorSnackbar(saveLogoError, saveLogoReset, i18n.t("profile.save_logo_error"));
    useApolloErrorSnackbar(
        savePrivateLogoError,
        savePrivateLogoReset,
        i18n.t("profile.save_logo_error"),
    );

    useEffect(() => {
        navigation &&
            navigation.setOptions({
                headerRight: () => (
                    <HeaderButton
                        onPress={() => void presentIntercom()}
                        label={i18n.t("assistance")}
                        style={{
                            alignItems: "center",
                            alignSelf: "flex-end",
                            marginRight: gridSize / 2,
                        }}
                    />
                ),
            });
    }, [colors.secondary, gridSize, i18n, navigation, presentIntercom]);

    const onThrowError = useCallback(() => {
        throw new Error("I told you not to click!");
    }, []);

    const acceptedClients = useMemo(
        () =>
            (user?.clientProfiles || []).filter(
                (clientProfile) => clientProfile.client.status === ClientStatus.Accepted,
            )?.length || 0,
        [user?.clientProfiles],
    );

    const validProfilesNb = useMemo(
        () => acceptedClients + (isWorkerProfileComplete ? 1 : 0),
        [acceptedClients, isWorkerProfileComplete],
    );

    const ijmGroup = currentProfile?.client?.ijmGroup;
    const canChangeIjmGroup = currentProfile?.client?.canChangeIjmGroup || false;
    const profileToUse = useMemo<ProfileToUse>(() => {
        if (isWorkerProfile) {
            return "Worker";
        }
        if (isClientProfile) {
            if (isPrivateClient) {
                return "PrivateClient";
            }
            return "Client";
        }
        return "Unknown";
    }, [isClientProfile, isPrivateClient, isWorkerProfile]);

    const baseComponents = useMemo(() => {
        return (
            <DocumentEditor
                visible={!!displayedDocument}
                document={displayedDocument}
                onClose={() => setDisplayedDocument(undefined)}
                onRemove={() => {
                    if (displayedDocument) {
                        void removeWorkerDocument(displayedDocument);
                        setDisplayedDocument(undefined);
                    }
                }}
            />
        );
    }, [displayedDocument, removeWorkerDocument]);

    const personalInfo = useMemo(() => {
        if (!user) {
            return null;
        }
        return (
            <>
                <ListTitle value={i18n.t("profile.personal_info")} />
                <ListItem
                    {...profilePicturePickerProps}
                    avatarPath={user.avatarUrl}
                    isAvatarLoading={updatingAvatar}
                    labels={[{ text: i18n.t("profile.change_profile_picture"), mode: "cta" }]}
                    rightActionMode="empty"
                    useBigAvatarOrIcon={true}
                />
                <ListItem
                    icon="user"
                    description={buildDescription([
                        i18n.t(`profile.title.${user.gender || "unknown"}`, {
                            firstName: user.firstName,
                            lastName: user.lastName,
                        }),
                        user.address?.formatted,
                        user.birthdate ? intlFormat(new Date(user.birthdate)) : undefined,
                        user.nationality
                            ? formatNationality(user.nationality, user.gender, "guess")
                            : undefined,
                        isWorkerProfile ? user.workerProfile?.avsNumber : undefined,
                        isWorkerProfile ? civilState : undefined,
                        user.workPermit
                            ? i18n.t(`work_permit_short.${user.workPermit}`)
                            : undefined,
                    ])}
                    labels={
                        isWorkerProfile && !user.workerProfile?.avsNumber
                            ? [
                                  {
                                      text: i18n.t("profile.missing_avs_number"),
                                      mode: "warning",
                                  },
                              ]
                            : []
                    }
                    onPress={isWorkerProfile ? editWorkerProfile : editClientProfile}
                />
            </>
        );
    }, [
        civilState,
        editClientProfile,
        editWorkerProfile,
        formatNationality,
        i18n,
        intlFormat,
        isWorkerProfile,
        profilePicturePickerProps,
        updatingAvatar,
        user,
    ]);

    const { edit: editIjm, saving: ijmSaving } = useProfileIjmEditor();

    const accountInfo = useMemo(() => {
        if (!user) {
            return null;
        }
        return (
            <>
                <ListTitle value={i18n.t("profile.user_account")} />
                <ListItem
                    icon="email"
                    description={
                        (isPrivateClient ? currentProfile?.businessEmail : user.email) || ""
                    }
                    onPress={isPrivateClient ? editPrivateClient : editAccount}
                    labels={
                        isPrivateClient || user.verified
                            ? []
                            : [
                                  {
                                      text: i18n.t("user.need_email_validation"),
                                      mode: "warning",
                                  },
                              ]
                    }
                />
                <ListItem
                    icon="phone"
                    description={user.phoneNumber || ""}
                    onPress={editPhoneNumber}
                />
                <ListItem
                    icon="activities"
                    onPress={toActivityLog}
                    description={i18n.t("profile.my_activity_log")}
                />
                <ListItem
                    onPress={openProfileSwitch}
                    rightActionMode="empty"
                    labels={[{ text: i18n.t("profile.change_profile"), mode: "cta" }]}
                />
                <ListItem
                    labels={[{ text: i18n.t("profile.logout"), mode: "cta" }]}
                    rightActionMode="empty"
                    onPress={onLogout}
                />
                <Gap gap={2} />
                <ListItem
                    labels={[{ text: i18n.t("profile.deleteAccount"), mode: "redCta" }]}
                    rightActionMode="empty"
                    onPress={onDeleteAccount}
                />
            </>
        );
    }, [
        currentProfile?.businessEmail,
        editAccount,
        editPhoneNumber,
        editPrivateClient,
        i18n,
        isPrivateClient,
        onDeleteAccount,
        onLogout,
        openProfileSwitch,
        toActivityLog,
        user,
    ]);

    const ijmInfo = useMemo(() => {
        return (
            <ListItem
                title={i18n.t("ijm.profile.title")}
                labels={
                    !ijmGroup && canChangeIjmGroup
                        ? [
                              {
                                  text: i18n.t("ijm.profile.to_complete"),
                                  mode: "miniCta",
                              },
                          ]
                        : []
                }
                description={
                    !ijmGroup && !canChangeIjmGroup
                        ? i18n.t("ijm.profile.missing_group")
                        : buildIjmDescription(
                              ijmGroup?.name,
                              currentProfile?.client?.ijmPayFirstDays,
                              currentProfile?.client?.ijmPayRemainingPercents,
                          )
                }
                icon="ijm"
                loading={ijmSaving}
                onPress={
                    canChangeIjmGroup ? editIjm : () => navigation?.navigate("IJMClientViewer")
                }
            />
        );
    }, [
        buildIjmDescription,
        canChangeIjmGroup,
        currentProfile?.client?.ijmPayFirstDays,
        currentProfile?.client?.ijmPayRemainingPercents,
        editIjm,
        i18n,
        ijmGroup,
        ijmSaving,
        navigation,
    ]);

    const colleaguesInfo = useMemo(() => {
        return (
            <ListItem
                icon="colleagues"
                onPress={toColleagues}
                description={i18n.t("profile.my_colleagues")}
            />
        );
    }, [i18n, toColleagues]);

    const versionInfo = useMemo(() => {
        return (
            <>
                {(Constants.expoConfig?.extra?.appEnv === "local" || devClickCount > 50) && (
                    <>
                        <Gap gap={2} />
                        <ListItem
                            labels={[{ text: `${i18n.t("profile.do_not_click")} 💣`, mode: "cta" }]}
                            rightActionMode="empty"
                            onPress={onThrowError}
                        />
                        <ListItem
                            labels={[
                                { text: `${i18n.t("profile.sample_skribble")} 💸💸`, mode: "cta" },
                            ]}
                            rightActionMode="empty"
                            onPress={() => {
                                navigation.navigate("SkribbleSampleScreen");
                            }}
                        />
                    </>
                )}
                <Gap gap={1} />
                <AppVersion
                    onPress={() => {
                        setDevClickCount(devClickCount + 1);
                    }}
                />
                <Gap gap={1} />
            </>
        );
    }, [devClickCount, i18n, navigation, onThrowError]);

    const talentInfo = useMemo(() => {
        if (!user) {
            return null;
        }
        return (
            <>
                <ListItem
                    icon="dollar-square"
                    description={buildDescription([
                        i18n.t("screens.profile_editor.bank_account.title"),
                        `IBAN: ${user?.workerProfile?.bankAccount?.iban}`,
                    ])}
                    labels={
                        user?.workerProfile?.bankAccount?.iban
                            ? []
                            : [
                                  {
                                      text: i18n.t("profile.missing_bank_account"),
                                      mode: "warning",
                                  },
                              ]
                    }
                    onPress={editBankAccount}
                />
                <ListItem
                    icon="buildings"
                    description={i18n.t("profile.client_list")}
                    onPress={() => {
                        navigation?.navigate("ClientList");
                    }}
                />
                <ListItem
                    icon="training"
                    description={
                        user?.workerProfile?.educationLevel
                            ? i18n.t(`education_level.${user?.workerProfile?.educationLevel}`)
                            : undefined
                    }
                    labels={
                        user?.workerProfile?.educationLevel
                            ? []
                            : [
                                  {
                                      text: i18n.t("worker_profile.errors.education_level"),
                                      mode: "warning",
                                  },
                              ]
                    }
                    onPress={editDiplomaAndTraining}
                />
                <ListItem
                    icon="experience"
                    description={
                        user?.workerProfile?.yearsOfExperience !== undefined
                            ? i18n.t("worker_details.years_of_experience", {
                                  count: user?.workerProfile?.yearsOfExperience || 0,
                              })
                            : undefined
                    }
                    labels={
                        user?.workerProfile?.yearsOfExperience
                            ? []
                            : [
                                  {
                                      text: i18n.t("worker_profile.errors.years_of_experience"),
                                      mode: "warning",
                                  },
                              ]
                    }
                    onPress={editDiplomaAndTraining}
                />
                <ListItem
                    key="ijm"
                    icon="ijm"
                    description={i18n.t("profile.ijm_document")}
                    onPress={() => {
                        void presentArticle("IJMTalentProfile");
                    }}
                />
                {user?.workerProfile?.documents?.map((document) => (
                    <ListItem
                        key={document.id}
                        icon="file"
                        description={document.title}
                        onPress={() => setDisplayedDocument(document)}
                    />
                ))}
                <DocumentButton
                    asListAction="primary"
                    label={i18n.t("profile.upload_document")}
                    onSelect={async (doc?: Document) => {
                        if (doc) {
                            await onAddWorkerDocument(DocumentType.Experience, doc);
                        }
                    }}
                    documentType={DocumentType.Experience}
                    disabled={removingDocument}
                    webUploadFn="workerDocuments"
                />
            </>
        );
    }, [
        editBankAccount,
        editDiplomaAndTraining,
        i18n,
        navigation,
        onAddWorkerDocument,
        presentArticle,
        removingDocument,
        user,
    ]);

    switch (profileToUse) {
        case "Worker": //
            return (
                <>
                    {baseComponents}
                    <SimplePageContent refresh={refreshUser}>
                        {personalInfo}
                        {talentInfo}
                        {accountInfo}
                        {validProfilesNb > 1 && (
                            <ListItem
                                labels={[
                                    { text: i18n.t("profile.delete.worker.title"), mode: "redCta" },
                                ]}
                                rightActionMode="empty"
                                onPress={() => {
                                    void onDeleteWorkerProfile();
                                }}
                            />
                        )}
                        {versionInfo}
                    </SimplePageContent>
                </>
            );
        case "Client": //
            return (
                <>
                    {baseComponents}
                    <SimplePageContent refresh={refreshUser}>
                        <ListTitle value={i18n.t("client_profile.client_title")} />
                        <ListItem
                            {...companyPicturePickerProps}
                            useBigAvatarOrIcon={true}
                            avatarPath={currentProfile?.client?.logoUrl}
                            isAvatarLoading={updatingLogo}
                            labels={[
                                { text: i18n.t("client_profile.change_client_logo"), mode: "cta" },
                            ]}
                            rightActionMode="empty"
                        />
                        <ListItem
                            description={buildDescription([
                                currentProfile?.client?.name,
                                currentProfile?.client?.address?.formatted,
                            ])}
                            onPress={editClient}
                            icon="buildings"
                        />
                        <ListTitle value={i18n.t("screens.profile_editor.administrative_data")} />
                        {ijmInfo}
                        {getClientUsers.length > 1 && colleaguesInfo}

                        {personalInfo}
                        {accountInfo}
                        {validProfilesNb > 1 && (
                            <ListItem
                                labels={[
                                    { text: i18n.t("profile.delete.client.title"), mode: "redCta" },
                                ]}
                                rightActionMode="empty"
                                onPress={() => {
                                    void onDeleteClientProfile();
                                }}
                            />
                        )}
                        {versionInfo}
                    </SimplePageContent>
                </>
            );
        case "PrivateClient": //
            return (
                <>
                    {baseComponents}
                    <SimplePageContent refresh={refreshUser}>
                        {personalInfo}
                        <ListTitle value={i18n.t("screens.profile_editor.administrative_data")} />
                        {ijmInfo}
                        {accountInfo}
                        {validProfilesNb > 1 && (
                            <ListItem
                                labels={[
                                    {
                                        text: i18n.t("profile.delete.private_client.title"),
                                        mode: "redCta",
                                    },
                                ]}
                                rightActionMode="empty"
                                onPress={() => {
                                    void onDeletePrivateClientProfile();
                                }}
                            />
                        )}
                        {versionInfo}
                    </SimplePageContent>
                </>
            );
        default:
            return (
                <VStack>
                    <Loading />
                </VStack>
            );
    }
};

export default ProfileScreen;
